import { Component, ElementRef, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { AuthService } from '@services/auth.service';
import { ThemeService, type Theme } from '@services/theme/theme.service';
import { getUserInitials } from '@utilities/initials';

interface ThemeOption {
  readonly value: Theme;
  readonly icon: string;
  readonly label: string;
}

@Component({
  selector: 'app-user-menu',
  imports: [RouterLink],
  templateUrl: './user-menu.html',
  styleUrl: './user-menu.css',
  host: {
    class: 'popover',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape($event)',
  },
})
export class UserMenu {
  private readonly authService = inject(AuthService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly themeService = inject(ThemeService);

  protected readonly isOpen = signal(false);

  protected readonly user = this.authService.user;

  protected readonly fullName = computed(() => {
    const user = this.user();
    return user ? `${user.firstName} ${user.lastName}`.trim() : 'Signed out';
  });

  protected readonly initials = computed(() => {
    const user = this.user();
    return user ? getUserInitials(user.firstName, user.lastName) : '';
  });

  protected readonly themeOptions: readonly ThemeOption[] = [
    { value: 'light', icon: 'sun', label: 'Light' },
    { value: 'dark', icon: 'moon', label: 'Dark' },
    { value: 'system', icon: 'desktop', label: 'System' },
  ];

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected onEscape(event: Event): void {
    if (!this.isOpen()) return;

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  protected setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  protected signOut(): void {
    this.close();
    this.authService.logout();
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;

    const target = event.target;
    if (target instanceof Node && !this.host.nativeElement.contains(target)) {
      this.close();
    }
  }
}
